/**
 * Role-based PostHog event helpers.
 *
 * Canonical roles from the API (`UserRole`):
 *   "regular-user" | "realtor" | "developer"
 *
 * PostHog event names use snake_case role prefixes:
 *   regular_user_logged_in / realtor_logged_in / developer_logged_in
 *   regular_user_signed_up / realtor_signed_up / developer_signed_up
 *   regular_user_onboarding_started / realtor_onboarding_started / ...
 *   regular_user_onboarding_completed / realtor_onboarding_completed / ...
 *
 * Callers should fire BOTH the generic event (existing funnels keep working)
 * and the per-role event (per-role funnels). `trackRoleFunnelEvent` does that.
 */

export type AnalyticsRole = "regular_user" | "realtor" | "developer";

export type RoleFunnelBase =
  | "logged_in"
  | "signed_up"
  | "onboarding_started"
  | "onboarding_completed";

/** "regular-user" -> "regular_user", "regular_user" -> "regular_user", else null. */
export function normalizeAnalyticsRole(
  role?: string | null,
): AnalyticsRole | null {
  if (!role) return null;
  const r = role.trim().toLowerCase().replace(/-/g, "_");
  if (r === "regular_user" || r === "regularuser" || r === "user" || r === "buyer_renter")
    return "regular_user";
  if (r === "realtor") return "realtor";
  if (r === "developer") return "developer";
  return null;
}

/** e.g. ("logged_in", "realtor") -> "realtor_logged_in". Null role -> null. */
export function roleEventName(
  base: RoleFunnelBase,
  role?: string | null,
): string | null {
  const normalized = normalizeAnalyticsRole(role);
  if (!normalized) return null;
  return `${normalized}_${base}`;
}

export type TrackEventFn = (
  event: string,
  payload?: Record<string, any>,
) => void;

/**
 * Fire the generic funnel event AND the per-role variant.
 *
 * - Generic keeps existing PostHog insights/funnels working:
 *     `user_logged_in` with `{ role: "realtor", method: "password" }`
 * - Per-role gives clean per-cohort counts without breakdowns:
 *     `realtor_logged_in` with the same payload
 *
 * If role is unknown, only the generic event fires (never drops data).
 */
export function trackRoleFunnelEvent(
  trackEvent: TrackEventFn,
  genericEvent: string,
  base: RoleFunnelBase,
  role: string | null | undefined,
  payload: Record<string, any> = {},
): void {
  const normalized = normalizeAnalyticsRole(role);
  const enriched = normalized
    ? { ...payload, role: normalized }
    : { ...payload };

  // 1) Generic — backwards compatible.
  trackEvent(genericEvent, enriched);

  // 2) Per-role — new per-cohort event.
  if (normalized) {
    trackEvent(`${normalized}_${base}`, enriched);
  }
}

/**
 * Same as trackRoleFunnelEvent but also fans out the GENERIC event to
 * GA/Mailchimp/backend via utils/track. The per-role variant is PostHog-only
 * so the backend marketing allow-list (`services/marketing`) needs no change.
 */
export function trackRoleFunnelEventDual(
  phTrackEvent: TrackEventFn,
  gaTrack: TrackEventFn,
  genericEvent: string,
  base: RoleFunnelBase,
  role: string | null | undefined,
  payload: Record<string, any> = {},
): void {
  trackRoleFunnelEvent(phTrackEvent, genericEvent, base, role, payload);
  const normalized = normalizeAnalyticsRole(role);
  try {
    gaTrack(genericEvent, normalized ? { ...payload, role: normalized } : payload);
  } catch {
    /* noop — analytics must never break auth flows */
  }
}
